import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   EventEmitter,
   Input,
   OnChanges,
   OnDestroy,
   OnInit,
   Output,
} from '@angular/core';
import {
   AbstractControl,
   FormBuilder,
   FormGroup,
   UntypedFormArray,
   UntypedFormGroup,
   ValidationErrors,
   ValidatorFn,
} from '@angular/forms';
//import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { TileLayoutConfigurationReportParameters } from '@ndt/reports/shared/report-parameter-list/models/report-parameter-union.model';
import { ReportParameterFormService } from '@ndt/reports/shared/report-parameter-list/services/report-parameter-form.service';
import { select, Store } from '@ngrx/store';
// import { NgChanges } from '@qmanage/core/ng-onchanges/ng-changes';
// import { TileLayoutConfigurationReportParameters } from '@qmanage/reports/shared/report-parameter-list/models/report-parameter-union.model';
// import { ReportParameterFormService } from '@qmanage/reports/shared/report-parameter-list/services/report-parameter-form.service';
// import { ColorPickerPresets } from '@qmanage/ui/color-picker/color-picker-presets';
import { Tiles } from 'generated/graphql-types';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NgChanges } from 'src/lib/ng-changes';
import { getRefreshTimeSecondsValue } from '../models/dashboard-tile-layout-configuration.model';
import { DashboardTile } from '../models/dashboard-tile.model';
import { TileSet, TileSetType } from '../tile-set-type-select/models/tile-set.model';
import { loadTileSet } from '../tile-set-type-select/state/tile-set-type-select.actions';
import { selectTileSet } from '../tile-set-type-select/state/tile-set-type-select.selectors';

export interface TileLayoutConfigurationFormValue {
   name: string | null;
   color: string | null;
   refreshTimeSeconds: number | null;
   tileSetType: TileSetType | null;
   canEditRefreshTime?: boolean;
   fields?: string[];
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ndt-tile-layout-configurations',
    styleUrls: ['./tile-layout-configurations.component.scss'],
    templateUrl: './tile-layout-configurations.component.html',
    standalone: false
})
export class TileLayoutConfigurationsComponent<P> implements OnInit, OnChanges, OnDestroy {
   @Input() tileConfig: DashboardTile;

   @Input() formService: ReportParameterFormService<P>;

   @Output() tileSetTypeChange = new EventEmitter<TileSetType>();

   @Output() tileLayoutConfigurationsInitialized = new EventEmitter<void>();

   //colorPresets: string[] = ColorPickerPresets;

   form: UntypedFormGroup;

   tileLayoutConfiguration: TileLayoutConfigurationFormValue;

   tileSet$: Observable<TileSet | null>;

   nameForm: FormGroup;

   get tileId(): Tiles {
      return this.tileConfig.tile.tileId;
   }

   get valueControls() {
      const valueControl = this.form?.get('values') as UntypedFormArray;
      return valueControl?.controls;
   }

   get nameControl() {
      return this.valueControls?.find((c) => c.value.parameterType === TileLayoutConfigurationReportParameters.Name);
   }

   get colorControl() {
      return this.valueControls?.find((c) => c.value.parameterType === TileLayoutConfigurationReportParameters.Color);
   }

   get refreshTimeSecondsControl() {
      return this.valueControls?.find(
         (c) => c.value.parameterType === TileLayoutConfigurationReportParameters.RefreshTimeSeconds
      );
   }

   get tileSetTypeControl() {
      return this.valueControls?.find(
         (c) => c.value.parameterType === TileLayoutConfigurationReportParameters.TileSetType
      );
   }

   private destroyed$ = new Subject<void>();

   constructor(private cd: ChangeDetectorRef, private store: Store, private fb: FormBuilder) {}

   ngOnInit(): void {
      this.store.dispatch(loadTileSet({ tileId: this.tileId }));
      this.tileSet$ = this.store.pipe(select(selectTileSet(this.tileId)));
   }

   ngOnChanges(changes: NgChanges<TileLayoutConfigurationsComponent<P>>): void {
      if (changes.formService) {
         if (!this.tileLayoutConfiguration) this.setTileConfiguration();

         this.formService.initLayoutConfigurationControls(this.tileLayoutConfiguration);
         this.tileLayoutConfigurationsInitialized.emit();

         const dirtyControls = this.valueControls?.filter((c) => c.dirty);

         this.form = this.formService.form;
         dirtyControls?.forEach((dc) => {
            const match = this.valueControls?.find((vc) => vc.value.parameterType === dc.value.parameterType);
            match?.setValue(dc.value, { emitEvent: false });
            match?.markAsDirty();
         });
         this.form.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
            this.setTileConfiguration();
            this.cd.markForCheck();
         });
      }
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   onBackgroundColorPickerOpen(): void {
      if (this.tileLayoutConfiguration.color === null) {
         this.colorControl?.patchValue({ color: '#2b376b' });
         this.colorControl?.markAsDirty();
      }
   }

   onColorChange(color: string) {
      this.colorControl?.patchValue({ color });
      this.colorControl?.markAsDirty();
   }

   onTileSetTypeChange(tileSetType: TileSetType) {
      this.tileSetTypeControl?.patchValue({ tileSetType });
      this.tileSetTypeControl?.markAsDirty();
      this.setTileConfiguration();
      this.tileSetTypeChange.emit(tileSetType);
   }

   setTileConfiguration() {
      const configuration = this.tileConfig.dashboardTileLayoutConfiguration;
      this.tileLayoutConfiguration = {
         name: this.nameControl?.value.name ?? configuration.name ?? null,
         color: this.colorControl?.value.color ?? configuration.color ?? '#2b376b',
         refreshTimeSeconds:
            getRefreshTimeSecondsValue(
               this.refreshTimeSecondsControl?.value.refreshTimeSeconds ?? configuration.refreshTimeSeconds
            ) ?? null,
         tileSetType: this.tileSetTypeControl?.value.tileSetType ?? null,
         canEditRefreshTime: this.tileConfig.tile.canEditRefreshTime ?? false,
      };
   }
   nameValidator(): ValidatorFn {
      return (control: AbstractControl): ValidationErrors | null => {
         const value = control.value.name;

         if (value && (value === '' || [...value].every((char) => char === ' '))) {
            return { invalidName: true };
         }

         return null;
      };
   }
}
